/**
 * Guest ID Verification Card
 * Allows guests to upload ID for instant verification
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Alert, AlertDescription } from '../ui/alert';
import { Separator } from '../ui/separator';
import { verifications } from '../../lib/api';
import { fileToBase64, validateIdDocument, formatFileSize } from '../../utils/fileUpload';
import { ShieldCheck, Upload, CheckCircle2, AlertCircle, Loader2, Shield, Info } from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL } from '@/lib/backendUrl';

export const GuestVerificationCard = ({ userEmail }) => {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [idType, setIdType] = useState('passport');
  const [privacyInfo, setPrivacyInfo] = useState(null);

  useEffect(() => {
    fetchStatus();
    fetchPrivacyInfo();
  }, [userEmail]);

  const fetchStatus = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${BACKEND_URL}/api/id-verify/status/${encodeURIComponent(userEmail)}`);
      const data = await res.json();
      setStatus({
        verified: !!data.age_verified,
        trust_score: data.trust_score || 0,
        verified_at: data.id_verified_at,
        min_age_met_bucket: data.min_age_met_bucket,
        issuing_country: data.issuing_country,
        id_expires_at: data.id_expires_at,
        id_name_match_level: data.id_name_match_level,
      });
    } catch (error) {
      console.error('Error fetching verification status:', error);
      toast.error('Failed to load verification status');
    } finally {
      setLoading(false);
    }
  };

  const fetchPrivacyInfo = async () => {
    try {
      const response = await verifications.getPrivacyInfo();
      setPrivacyInfo(response.data.privacy_policy);
    } catch (error) {
      console.error('Error fetching privacy info:', error);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validation = validateIdDocument(file);
    if (!validation.valid) {
      toast.error(validation.error);
      e.target.value = '';
      return;
    }

    setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file to upload');
      return;
    }

    try {
      setUploading(true);

      // Convert to raw base64 (strip data:image/...; prefix)
      const dataUrl = await fileToBase64(selectedFile);
      const rawB64 = String(dataUrl).split(',')[1] || dataUrl;

      // Privacy-first endpoint. booking_name is optional — portal-time verify
      // skips name-match (it happens at booking time against the booking row).
      const res = await fetch(`${BACKEND_URL}/api/id-verify/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_email: userEmail,
          image_base64: rawB64,
          booking_name: null,
        }),
      });
      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.detail || 'Verification failed');
      }

      if (data.success && data.age_valid) {
        toast.success('ID verified · Trusted Guest SBT active');
        setSelectedFile(null);
        await fetchStatus();
      } else {
        toast.error(data.error || 'Verification failed');
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.message || 'Failed to upload ID');
    } finally {
      setUploading(false);
    }
  };

  const handleRevoke = async () => {
    if (!confirm('Are you sure you want to revoke your verification? This action cannot be undone.')) {
      return;
    }

    try {
      setLoading(true);
      await verifications.revokeVerification(userEmail);
      toast.success('Verification revoked successfully');
      await fetchStatus();
    } catch (error) {
      console.error('Revoke error:', error);
      toast.error('Failed to revoke verification');
    } finally {
      setLoading(false);
    }
  };

  if (loading && !status) {
    return (
      <Card data-testid="guest-verification-card">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            <CardTitle>ID Verification</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const isVerified = status?.verified;

  return (
    <Card data-testid="guest-verification-card" className="border-border">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck className={`w-5 h-5 ${
              isVerified ? 'text-emerald-500' : 'text-muted-foreground'
            }`} />
            <CardTitle>ID Verification</CardTitle>
          </div>
          {isVerified && (
            <Badge variant="secondary" className="bg-emerald-500/10 text-emerald-600 border-emerald-500/20">
              <CheckCircle2 className="w-3 h-3 mr-1" />
              Verified
            </Badge>
          )}
        </div>
        <CardDescription>
          {isVerified 
            ? 'Your identity has been verified'
            : 'Verify your identity to increase trust and unlock premium features'
          }
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-4">
        {isVerified ? (
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-emerald-500/5 rounded-lg border border-emerald-500/20">
              <div>
                <p className="text-sm font-medium">Trust Score</p>
                <p className="text-muted-foreground text-xs mt-1">
                  Verified on {new Date(status.verified_at).toLocaleDateString()}
                </p>
              </div>
              <div className="text-2xl heading-font text-emerald-600">
                {status.trust_score || 80}/100
              </div>
            </div>

            <Alert data-testid="verification-privacy-info">
              <Info className="w-4 h-4" />
              <AlertDescription className="text-xs">
                Your ID photo was deleted immediately after verification. We only store your verification status and trust score.
              </AlertDescription>
            </Alert>

            <Button
              variant="outline"
              onClick={handleRevoke}
              disabled={loading}
              data-testid="revoke-verification-btn"
              className="w-full"
            >
              Revoke Verification
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Privacy Notice */}
            {privacyInfo && (
              <Alert data-testid="privacy-notice">
                <Info className="w-4 h-4" />
                <AlertDescription className="text-xs space-y-1">
                  <p className="font-medium">Privacy-First Verification</p>
                  <ul className="list-disc list-inside space-y-1 text-muted-foreground">
                    <li>Your ID photo is deleted immediately after verification (~1 minute)</li>
                    <li>We never store your ID number or sensitive details</li>
                    <li>AES-256 encryption during upload</li>
                    <li>GDPR compliant - You can revoke anytime</li>
                  </ul>
                </AlertDescription>
              </Alert>
            )}

            {/* ID Type Selection */}
            <div className="space-y-2">
              <Label htmlFor="id-type">ID Document Type</Label>
              <Select value={idType} onValueChange={setIdType}>
                <SelectTrigger data-testid="id-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="passport">Passport</SelectItem>
                  <SelectItem value="driver_license">Driver's License</SelectItem>
                  <SelectItem value="national_id">National ID</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* File Upload */}
            <div className="space-y-2">
              <Label htmlFor="id-upload">Upload ID Photo</Label>
              <Input
                id="id-upload"
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/webp"
                onChange={handleFileSelect}
                disabled={uploading}
                data-testid="id-upload-input"
              />
              {selectedFile && (
                <p className="text-xs text-muted-foreground">
                  Selected: {selectedFile.name} ({formatFileSize(selectedFile.size)})
                </p>
              )}
            </div>

            {/* Upload Button */}
            <Button
              onClick={handleUpload}
              disabled={!selectedFile || uploading}
              className="w-full"
              data-testid="upload-id-btn"
            >
              {uploading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Verifying...
                </>
              ) : (
                <>
                  <Upload className="w-4 h-4 mr-2" />
                  Upload & Verify
                </>
              )}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
};